import React, { useEffect, useRef, useState } from "react";

import { useForm } from "../useForm";

import NewPackageDetails1 from "./newComponents/NewPackageDetails1";
import NewPackageDetails2 from "./newComponents/NewPackageDetails2";
import ModelErrorComponent from "../../modals/ModelErrorComponent";
import ReceivedMaterialsForPackaging from "./newComponents/ReceivedMaterialsForPackaging";
import ListOfBrandsTable from "./newComponents/ListOfBrandsTable";
import WeightScalePad from "../../controls/WeightScalePad";
import WeightScale from "../../controls/WeightScale";
import Button from "../../ui/Button";
import PrintProgressModal from "./PrintProgressModal";
import { convertToTwoDecimals } from "../../../shared/utility";
import { readDefaultLocationFromStorage } from "../../../shared/defaultLocationStorage";
import {
  fetchLocationsForDropdown,
  fetchLocationZonesForDropdown,
  normalizeDropdownOption,
} from "../../../shared/locationDropdowns";
import { computeBestBeforeDate } from "./bestBeforeDate";

import { io } from "socket.io-client";

function temperatureError(value: unknown, requiredMessage: string) {
  const text = value == null ? "" : String(value).trim();
  if (text === "") {
    return requiredMessage;
  }
  if (!Number.isFinite(Number(text))) {
    return "Enter a number.";
  }
  return null;
}

const initialFValues = {
  product_id: -1,
  production_product_id: -1,
  packaging_date: new Date(),
  production_date: "",
  best_before_date: "",
  shelf_life: "",
  calculate_best_before_from_packaging: false,
  serial_number: "",
  supervisor: [],
  employee_1: [],
  employee_2: [],
  room_temperature: "",
  product_temperature: "",
  barcode: "",
  description_en: "",
  plu: "",
  product_name: "",
  production_run_plu: "",
  production_run_product_name: "",
  packaging_material_1: "",
  packaging_material_2: "",
  number_of_label_to_print: 1,
  brand_id: -1,
  brand_name: null,
  is_default_brand: false,
  is_create_pallet: false,
  passed_from_metal_detector: true,
  lot_number: "",
  is_scale: false,
  location: null,
  zone: null,
};

export function getManufacturedProductIdFromOption(option: any): number | null {
  if (!option || option.value === -1) return null;
  return (
    option.manufactured_product_id ??
    option.production_product_id ??
    option.production_id ??
    option.id ??
    option.value ??
    null
  );
}

export function getLotFromOption(option: any): string | null {
  if (!option) return null;
  if (option.lot_number) return option.lot_number;
  if (typeof option.label === "string") {
    const fromLabel = option.label.split(" - ")[0]?.trim();
    if (fromLabel) return fromLabel;
  }
  return null;
}

export function applyOptionPreviewToValues(option: any, prev: any) {
  if (!option || option.value === -1) return prev;
  const lot = getLotFromOption(option);
  const productionDate = option.production_date ?? prev.production_date;
  const shelfLife = option.shelf_life ?? prev.shelf_life;
  const fromPackaging = Boolean(
    option.calculate_best_before_from_packaging ??
    prev.calculate_best_before_from_packaging,
  );
  let bestBeforeDate =
    option.shelf_life_date ?? option.best_before_date ?? prev.best_before_date;

  const computedBestBeforeDate = computeBestBeforeDate({
    shelfLife,
    productionDate,
    packagingDate: prev.packaging_date,
    fromPackaging,
  });
  if (computedBestBeforeDate) {
    bestBeforeDate = computedBestBeforeDate;
  }

  return {
    ...prev,
    production_run_plu: option.plu ?? prev.production_run_plu,
    production_run_product_name:
      option.product_name ?? option.name ?? prev.production_run_product_name,
    lot_number: lot ?? prev.lot_number,
    plu: option.plu ?? prev.plu,
    product_name: option.product_name ?? option.name ?? prev.product_name,
    description_en: option.description_en ?? prev.description_en,
    production_date: productionDate,
    shelf_life: shelfLife,
    calculate_best_before_from_packaging: fromPackaging,
    best_before_date: bestBeforeDate,
    packing_size: option.packing_size ?? prev.packing_size,
  };
}

export function resolvePackagingLocationDefaults(data: any) {
  const productLocation = normalizeDropdownOption(data?.product?.location);
  const productZone = normalizeDropdownOption(data?.product?.zone);

  if (productLocation?.value) {
    return {
      location: productLocation,
      zone: productZone,
    };
  }

  return {
    location: readDefaultLocationFromStorage(),
    zone: null,
  };
}

function applyAssignedProductFromManufacturedData(data: any, prev: any) {
  const assigned = data?.product;
  if (!assigned) return {};

  return {
    product_id: assigned.id ?? prev.product_id,
    plu: assigned.plu ?? prev.plu,
    product_name:
      assigned.name_en ?? assigned.product_name ?? prev.product_name,
    description_en: assigned.description_en ?? prev.description_en,
    packing_size: assigned.weight ?? assigned.packing_size ?? prev.packing_size,
    shelf_life: assigned.shelf_life ?? prev.shelf_life,
    calculate_best_before_from_packaging: Boolean(
      assigned.calculate_best_before_from_packaging ??
      data.calculate_best_before_from_packaging ??
      prev.calculate_best_before_from_packaging,
    ),
    ...resolvePackagingLocationDefaults({ product: assigned }),
  };
}

function getPackingSizeFromManufacturedData(data: any) {
  const assigned = data?.product;
  if (assigned?.weight != null && assigned.weight !== "") {
    return assigned.weight;
  }
  if (assigned?.packing_size != null && assigned.packing_size !== "") {
    return assigned.packing_size;
  }
  return data?.packing_size;
}

export function applyManufacturedItemDataToValues(data: any, prev: any) {
  const isFreeWeight = !(parseFloat(data.packing_size) > 0.0);
  const fromPackaging = Boolean(
    data.calculate_best_before_from_packaging ??
    data.product?.calculate_best_before_from_packaging,
  );
  let bestBeforeDate = data.shelf_life_date ?? data.best_before_date ?? "";

  const computedBestBeforeDate = computeBestBeforeDate({
    shelfLife: data.shelf_life,
    productionDate: data.production_date,
    packagingDate: prev.packaging_date,
    fromPackaging,
  });
  if (computedBestBeforeDate) {
    bestBeforeDate = computedBestBeforeDate;
  }

  return {
    ...prev,
    production_product_id: data.id,
    user_id: data.user_id,
    product_id: data.product_id,
    production_date: data.production_date,
    best_before_date: bestBeforeDate,
    is_free_weight: isFreeWeight,
    packing_size: data.packing_size,
    shelf_life: data.shelf_life,
    calculate_best_before_from_packaging: fromPackaging,
    description_en: data.description_en,
    product_barcode: data.product_barcode ?? data.barcode,
    brand: data.brand,
    plu: data.plu,
    product_name: data.product_name,
    production_run_plu: data.plu,
    production_run_product_name: data.product_name,
    lot_number: data.lot_number,
    export_eligible: data.export_eligible,
    number_of_label_to_print: isFreeWeight ? 1 : prev.number_of_label_to_print,
    ...applyAssignedProductFromManufacturedData(data, prev),
  };
}

async function fetchManufacturedItem(
  selectedOption: any,
  getById: (id: number) => Promise<any>,
) {
  const id = getManufacturedProductIdFromOption(selectedOption);
  if (id == null) return null;
  return getById(id);
}

export default function NewPackageForm(props: any) {
  const {
    employeeOptions,
    isEdit,
    getManufacturedItemById,
    packagedProduct,
    setEmptyModelOpen,
    setSlideOver,
    savePackagingDetailsAndPrint,
    brands,
    showErrorModal,
    setScaleIsOpen,
    scaleIsOpen,
    printedPackageItems,
    deleteSinglePackagedItem,
    reprintPackageLabel,
    fetchProductByPlu,
    printProgress,
    getInContactPackagingSupplies,
    deleteInProgress,
    supervisorOptions,
    lotNumbers,
    onSearchLotNumbers,
  } = props;

  const [barcodeScanInProgress, setBarcodeScanInProgress] = useState(false);
  const defaultScaleData = {
    grossWeight: 0.0,
    packing_size: 0.0,
    bgStable: false,
    isLbs: false,
    typeName: "kg",
  };
  const [scaleData, setScaleData] = useState(defaultScaleData);
  const [showNumberPad, setShowNumberPad] = useState({
    show: false,
    name: null,
  });
  const [showWeightScalePad, setShowWeightScalePad] = useState({
    show: false,
    name: null,
  });
  const [tare, setTare] = useState(0);
  const [locationOptions, setLocationOptions] = useState<any[]>([]);
  const [zoneOptions, setZoneOptions] = useState<any[]>([]);
  const lotFetchSeqRef = useRef(0);

  const validate = (fieldValues: any = values) => {
    let temp = { ...errors };

    temp.production_product_id = null;
    if (values.production_product_id < 1) {
      temp.production_product_id = "Please enter lot number";
      showErrorModal("Please enter lot number");
    }

    temp.packaging_date = null;
    if (values.packaging_date === "") {
      temp.packaging_date = "Please select a date.";
    }

    temp.supervisor = null;
    if (values.supervisor.length < 1) {
      temp.supervisor = "Please select a supervisor.";
    }

    temp.brand_id = null;
    if (values.brand_id < 1) {
      temp.brand_id = "Please choose a brand.";
      showErrorModal("Please select a brand");
    }

    temp.number_of_label_to_print = null;
    if (
      values.number_of_label_to_print === "" ||
      parseInt(values.number_of_label_to_print) < 1
    ) {
      temp.number_of_label_to_print = "Please select a number more than 0.";
    }

    temp.room_temperature = temperatureError(
      values.room_temperature,
      "Please enter room temperature.",
    );

    temp.product_temperature = temperatureError(
      values.product_temperature,
      "Please enter product temperature.",
    );

    temp.location = null;
    if (!values.location?.value) {
      temp.location = "Please select a location.";
      showErrorModal("Please select a location.");
    }

    temp.zone = null;
    if (!values.zone?.value) {
      temp.zone = "Please select a zone.";
      showErrorModal("Please select a zone.");
    }

    setErrors({
      ...temp,
    });

    if (fieldValues === values) {
      return Object.values(temp).every((x) => x === null);
    }
  };

  const { values, setValues, errors, setErrors, handleInputChange } = useForm(
    initialFValues,
    false,
    validate,
  ) as any;

  useEffect(() => {
    if (!values.calculate_best_before_from_packaging) return;

    const bestBeforeDate = computeBestBeforeDate({
      shelfLife: values.shelf_life,
      productionDate: values.production_date,
      packagingDate: values.packaging_date,
      fromPackaging: true,
    });
    if (!bestBeforeDate || bestBeforeDate === values.best_before_date) return;

    setValues((prev: any) => ({
      ...prev,
      best_before_date: bestBeforeDate,
    }));
  }, [
    values.calculate_best_before_from_packaging,
    values.packaging_date,
    values.shelf_life,
    values.production_date,
    values.best_before_date,
    setValues,
  ]);

  const onLotNumberSelected = async function (selectedOption: any) {
    if (!selectedOption || selectedOption.value === -1) {
      return;
    }

    const fetchSeq = ++lotFetchSeqRef.current;

    setValues((prev: any) => applyOptionPreviewToValues(selectedOption, prev));
    if (
      selectedOption.packing_size != null &&
      selectedOption.packing_size !== ""
    ) {
      setScaleData((prev: any) => ({
        ...prev,
        packing_size: selectedOption.packing_size,
      }));
    }

    setBarcodeScanInProgress(true);

    const fetchedItem = await fetchManufacturedItem(
      selectedOption,
      getManufacturedItemById,
    );

    if (fetchSeq !== lotFetchSeqRef.current) {
      setBarcodeScanInProgress(false);
      return;
    }

    if (fetchedItem?.data) {
      setValues((prev: any) =>
        applyManufacturedItemDataToValues(fetchedItem.data, prev),
      );
      const packingSize = getPackingSizeFromManufacturedData(fetchedItem.data);
      setScaleData({
        ...defaultScaleData,
        packing_size: packingSize,
      });
    } else {
      setEmptyModelOpen({
        childComponent: (
          <ModelErrorComponent
            setOpen={setEmptyModelOpen}
            subtitle="We could not find that product!"
            title="Error"
          />
        ),
        open: true,
      });
    }

    setBarcodeScanInProgress(false);
  };

  const openPackagingOptions = (item_name: string) => {
    setSlideOver({
      childComponent: (
        <ReceivedMaterialsForPackaging
          getInContactPackagingSupplies={getInContactPackagingSupplies}
          setOpen={setSlideOver}
          setItemValues={(packagingItem: string) => {
            let packagingObj: Record<string, string> = {};
            if (item_name === "packaging_material_1") {
              packagingObj = { packaging_material_1: packagingItem };
            } else if (item_name === "packaging_material_2") {
              packagingObj = { packaging_material_2: packagingItem };
            } else if (item_name === "packaging_material_3") {
              packagingObj = { packaging_material_3: packagingItem };
            }

            setValues((prev: any) => ({
              ...prev,
              ...packagingObj,
            }));
          }}
        />
      ),
      open: true,
    });
  };

  const handleSavePackageAndPrint = async function (
    extraPayload: Record<string, unknown> = {},
  ) {
    if (validate()) {
      await savePackagingDetailsAndPrint({
        ...values,
        location_id: values.location?.value,
        zone_id: values.zone?.value ?? null,
        is_scale: scaleIsOpen,
        packing_size: scaleData.packing_size,
        ...extraPayload,
      });
    }
  };

  const handleLocationChange = (e: any) => {
    setValues((prev: any) => ({
      ...prev,
      location: e.target.value,
      zone: null,
    }));
  };

  useEffect(() => {
    let cancelled = false;

    async function loadLocations() {
      const callResult: any = await fetchLocationsForDropdown();
      if (!cancelled && callResult.success) {
        setLocationOptions(callResult.data ?? []);
      }
    }

    loadLocations();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!values.location?.value) {
      setZoneOptions([]);
      return;
    }

    let cancelled = false;

    async function loadZones() {
      const callResult: any = await fetchLocationZonesForDropdown(
        values.location,
      );
      if (!cancelled && callResult.success) {
        setZoneOptions(callResult.data ?? []);
      }
    }

    loadZones();

    return () => {
      cancelled = true;
    };
  }, [values.location?.value]);

  useEffect(() => {
    if (values.location?.value || values.zone?.value) {
      return;
    }

    const defaultLocation = readDefaultLocationFromStorage();
    if (defaultLocation) {
      setValues((prev: any) => ({ ...prev, location: defaultLocation }));
    }
  }, [values.location?.value, values.zone?.value, setValues]);

  const showBrands = () => {
    setSlideOver({
      childComponent: (
        <ListOfBrandsTable
          brands={brands}
          setItemValues={(brand_id: number, brand_name: string) => {
            setValues((prev: any) => ({
              ...prev,
              brand_id: brand_id,
              brand_name: brand_name,
            }));
            setSlideOver({
              open: false,
              childComponent: null,
            });
          }}
        />
      ),
      open: true,
      size: "max-w-xl",
    });
  };

  useEffect(() => {
    brands.data.forEach((brand: any) => {
      if (brand.is_default_brand === 1) {
        setValues((prev: any) => ({
          ...prev,
          brand_id: brand.id,
          brand_name: brand.name,
          is_default_brand: brand.is_default_brand === 1,
        }));
      }
    });
  }, [brands]);

  function handleIncomingScaleData(data: { weight: string }) {
    var newWeight = "";

    const rawWeight = data.weight;
    const trimmedValue = rawWeight.substring(rawWeight.lastIndexOf(" ") + 1);

    // this is for seeing if kg scale is stable
    var isKgStable = trimmedValue.charAt(trimmedValue.length - 1) !== "M";
    // LG stuff is for weighing by lb
    var isLGM =
      trimmedValue.substring(trimmedValue.length - 3, trimmedValue.length) ===
      "LGM";
    var isLG =
      trimmedValue.substring(trimmedValue.length - 2, trimmedValue.length) ===
      "LG";

    var isLbs = true;
    var isBgStable = false;
    var typeName = "lbs";
    var newTareWeight = tare;
    if (trimmedValue.length > 0) {
      var amountToSubtract = isKgStable ? 0 : 1;
      if (isLGM) {
        newWeight = trimmedValue.substring(0, trimmedValue.length - 3) + "LB";
      } else if (isLG) {
        newWeight = trimmedValue.substring(0, trimmedValue.length - 2) + "LB";
      } else {
        newWeight = trimmedValue.substring(
          0,
          trimmedValue.length - amountToSubtract,
        );

        isLbs = false;
        typeName = "kg";
      }
      isBgStable = isKgStable || isLG;

      if (values.isLbs !== isLbs) {
        if (isLbs) {
          newTareWeight = newTareWeight * 2.205;
        } else {
          newTareWeight = newTareWeight / 2.205;
        }
        setTare(newTareWeight);
      }

      setScaleData({
        ...scaleData,
        packing_size: convertToTwoDecimals(
          parseFloat(newWeight) - newTareWeight,
        ),
        grossWeight: convertToTwoDecimals(
          parseFloat(newWeight + newTareWeight),
        ),
        bgStable: isBgStable,
        isLbs: isLbs,
        typeName: typeName,
      });
    }
  }

  useEffect(() => {
    if (scaleIsOpen) {
      const newSocket = io("http://localhost:5001");

      newSocket.on("scale_data", (data: { weight: string }) => {
        handleIncomingScaleData(data);
      });

      newSocket.on("error", (data: { error: unknown }) => {
        console.log("SOCKET_ERROR: ", data.error);
        // showErrorModal("SOCKET_ERROR: ", data.error);
      });

      newSocket.emit("start_listening", {});
      return () => {
        newSocket.close();
      };
    }
  }, [scaleIsOpen, tare]);

  return (
    <div className="px-4 sm:px-6 lg:px-4">
      <PrintProgressModal
        open={printProgress.loading || barcodeScanInProgress}
        message={
          printProgress.loading
            ? printProgress.message
            : "Loading production run..."
        }
      />
      <div className="sm:flex sm:items-center px-4 py-5 sm:px-4 card-custom">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            {values.brand_name
              ? `Packaging Product for ${values.brand_name}`
              : "Package new product, please select brand"}
          </h1>
        </div>
      </div>
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <NewPackageDetails1
          employeeOptions={employeeOptions}
          supervisorOptions={supervisorOptions}
          errors={errors}
          isEdit={isEdit}
          onSearchLotNumbers={onSearchLotNumbers}
          onLotNumberSelected={onLotNumberSelected}
          handleInputChange={handleInputChange}
          packagedProduct={packagedProduct}
          values={values}
          openPackagingOptions={openPackagingOptions}
          handleSavePackageAndPrint={handleSavePackageAndPrint}
          showNumberPad={showNumberPad}
          setShowNumberPad={setShowNumberPad}
          showBrands={showBrands}
          barcodeScanInProgress={barcodeScanInProgress}
          setScaleIsOpen={setScaleIsOpen}
          scaleIsOpen={scaleIsOpen}
          showErrorModal={showErrorModal}
          printInProgress={printProgress.loading}
          lotNumbers={lotNumbers}
          setEmptyModelOpen={setEmptyModelOpen}
        />
        <NewPackageDetails2
          errors={errors}
          handleInputChange={handleInputChange}
          handleLocationChange={handleLocationChange}
          locationOptions={locationOptions}
          zoneOptions={zoneOptions}
          values={values}
          showNumberPad={showNumberPad}
          setShowNumberPad={setShowNumberPad}
          fetchProductByPlu={fetchProductByPlu}
          setValues={setValues}
          scaleData={scaleData}
          setScaleData={setScaleData}
          resolvePackagingLocationDefaults={resolvePackagingLocationDefaults}
        />
        <WeightScalePad
          showWeightScalePad={showWeightScalePad}
          setShowWeightScalePad={setShowWeightScalePad}
          values={values}
        />
        <div className="gap-2">
          {scaleIsOpen ? <WeightScale scaleData={scaleData} /> : null}
          {/* <div className="mt-5">
            <Numberpad
              showNumberPad={showNumberPad}
              setShowNumberPad={setShowNumberPad}
              values={values}
            />
          </div> */}
        </div>
      </div>
      <div className="mt-5">
        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="overflow-hidden card-custom">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-white">
                    <tr>
                      <th
                        scope="col"
                        className="py-3 pl-3 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                      >
                        Serial Number
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        PLU
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Lot Number
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Name
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      >
                        Description
                      </th>
                      <th
                        scope="col"
                        className="relative py-3.5 pl-3 pr-4 sm:pr-6"
                      ></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {printedPackageItems.length > 0 ? (
                      printedPackageItems.map((assemblyItem: any) => (
                        <tr key={assemblyItem.package_id}>
                          <td className="whitespace-nowrap py-1.5 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                            {assemblyItem.serial_number}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {assemblyItem.plu}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {assemblyItem.lot_number}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {assemblyItem.name}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {assemblyItem.description}
                          </td>
                          <td className="relative whitespace-nowrap py-1.5 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <Button
                              type="button"
                              variant="secondary"
                              className="!border-[#7C3AED] !bg-[#7C3AED] !text-white hover:!border-[#6D28D9] hover:!bg-[#6D28D9] hover:!text-white"
                              onClick={() => {
                                reprintPackageLabel(assemblyItem);
                              }}
                              disabled={false}
                            >
                              Print again
                            </Button>
                            <Button
                              type="button"
                              variant="danger"
                              className="ml-2"
                              onClick={() => {
                                deleteSinglePackagedItem(
                                  assemblyItem.package_id,
                                );
                              }}
                              loading={deleteInProgress}
                            >
                              Delete
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="py-5 text-center">
                          Please start scanning products
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
